import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useDemographicsStore } from "@/store/demographics";
import type { IDemographics } from "@/store/demographics";
import { useProfileStore, getFacilityKey } from "@/store/profile";
import { toast } from "sonner";
import { normalizeSexFromApi } from "@/utils/demographicsSex";

export type ProfileInfoMode = "profile" | "wizard";

export const DEFAULT_PATIENT: Record<string, string> = {
  firstName: "",
  mi: "",
  lastName: "",
  nickname: "",
  dob: "",
  sex: "",
  maritalStatID: "",
  address: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  mobilePhone: "",
  homePhone: "",
  email: "",
  contactMethod: "",
  emerContact: "",
  emerRelation: "",
  emerPhone: "",
  employStatID: "",
};

/** Required demographics fields (must be non-empty to continue from wizard). */
export const REQUIRED_DEMOGRAPHICS_FIELDS = [
  "firstName",
  "lastName",
  "dob",
  "sex",
  "address",
  "city",
  "state",
  "zip",
  "email",
  "emerContact",
  "emerRelation",
  "emerPhone",
] as const;

/** True when value contains exactly 10 digits (US phone). */
export function isTenDigitPhone(value: string | undefined): boolean {
  return (value ?? "").replace(/\D/g, "").length === 10;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** True when value looks like a valid email address. */
export function isValidEmail(value: string | undefined): boolean {
  return EMAIL_RE.test((value ?? "").trim());
}

/** Mask free text into MM/DD/YYYY as the user types (digits only, auto-slashes). */
export const DOB_MASK = (v: string): string => {
  const d = (v ?? "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

/**
 * True when value is a real calendar date in MM/DD/YYYY form, not in the future,
 * with a plausible year (>= 1900). Rejects impossible days (e.g. 02/30) and partials.
 */
export function isValidDob(value: string | undefined): boolean {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((value ?? "").trim());
  if (!m) return false;
  const mo = Number(m[1]);
  const da = Number(m[2]);
  const yr = Number(m[3]);
  if (mo < 1 || mo > 12) return false;
  if (yr < 1900) return false;
  const dt = new Date(yr, mo - 1, da);
  if (dt.getFullYear() !== yr || dt.getMonth() !== mo - 1 || dt.getDate() !== da) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dt.getTime() <= today.getTime();
}

export function hasAtLeastOneDemographicsPhone(
  patient: Record<string, string>
): boolean {
  return (
    isTenDigitPhone(patient.mobilePhone) || isTenDigitPhone(patient.homePhone)
  );
}

export function isRequiredDemographicsComplete(
  patient: Record<string, string>
): boolean {
  const fieldsOk = REQUIRED_DEMOGRAPHICS_FIELDS.every(
    (key) => (patient[key] ?? "").trim() !== ""
  );
  return fieldsOk && hasAtLeastOneDemographicsPhone(patient);
}

export interface DemographicsFieldError {
  field: string;
  message: string;
}

/**
 * Validate the fields that must be well-formed before saving, in top-to-bottom
 * field order so the first entry is the one to focus. Format checks only fire
 * when the field has a value; the "at least one phone" rule always applies.
 */
export function collectDemographicsErrors(
  patient: Record<string, string>
): DemographicsFieldError[] {
  const errs: DemographicsFieldError[] = [];
  const dob = (patient.dob ?? "").trim();
  if (dob && !isValidDob(dob)) {
    errs.push({ field: "dob", message: "Enter a valid date of birth (MM/DD/YYYY)." });
  }
  const zip = (patient.zip ?? "").replace(/\D/g, "");
  if (zip && zip.length !== 5 && zip.length !== 9) {
    errs.push({ field: "zip", message: "Enter a valid ZIP code (5 or 9 digits)." });
  }
  const mobile = (patient.mobilePhone ?? "").trim();
  if (mobile && !isTenDigitPhone(mobile)) {
    errs.push({ field: "mobilePhone", message: "Enter a valid 10-digit mobile phone number." });
  }
  const home = (patient.homePhone ?? "").trim();
  if (home && !isTenDigitPhone(home)) {
    errs.push({ field: "homePhone", message: "Enter a valid 10-digit home phone number." });
  }
  if (!hasAtLeastOneDemographicsPhone(patient)) {
    errs.push({
      field: "mobilePhone",
      message: "Enter at least one valid 10-digit phone number (mobile or home).",
    });
  }
  const email = (patient.email ?? "").trim();
  if (email && !isValidEmail(email)) {
    errs.push({ field: "email", message: "Enter a valid email address." });
  }
  const emer = (patient.emerPhone ?? "").trim();
  if (emer && !isTenDigitPhone(emer)) {
    errs.push({
      field: "emerPhone",
      message: "Enter a valid 10-digit emergency contact phone number.",
    });
  }
  return errs;
}

export interface DemographicsOutstanding {
  field: string;
  /** Short, human name for the summary chip near Save. */
  label: string;
  /** Full sentence shown beneath the field. */
  message: string;
}

/**
 * Everything still standing between the patient and a valid save, in top-to-bottom
 * visual order (so the first entry is the one to scroll to). Unlike
 * `collectDemographicsErrors`, this also reports *empty* required fields — that's
 * what powers the inline "Required" messages and the "needs attention" summary.
 * When this returns an empty array, the form is complete and well-formed.
 */
export function collectOutstandingDemographics(
  patient: Record<string, string>
): DemographicsOutstanding[] {
  const out: DemographicsOutstanding[] = [];
  const val = (k: string) => (patient[k] ?? "").trim();

  if (!val("firstName"))
    out.push({ field: "firstName", label: "First Name", message: "First name is required." });
  if (!val("lastName"))
    out.push({ field: "lastName", label: "Last Name", message: "Last name is required." });

  if (!val("dob"))
    out.push({ field: "dob", label: "Date of Birth", message: "Date of birth is required." });
  else if (!isValidDob(val("dob")))
    out.push({ field: "dob", label: "Date of Birth", message: "Enter a valid date of birth (MM/DD/YYYY)." });

  if (!val("sex"))
    out.push({ field: "sex", label: "Gender", message: "Gender is required." });
  if (!val("address"))
    out.push({ field: "address", label: "Street Address", message: "Street address is required." });
  if (!val("city"))
    out.push({ field: "city", label: "City", message: "City is required." });
  if (!val("state"))
    out.push({ field: "state", label: "State", message: "State is required." });

  const zip = val("zip").replace(/\D/g, "");
  if (!zip)
    out.push({ field: "zip", label: "Zip", message: "ZIP code is required." });
  else if (zip.length !== 5 && zip.length !== 9)
    out.push({ field: "zip", label: "Zip", message: "Enter a valid ZIP code (5 or 9 digits)." });

  const mobile = val("mobilePhone");
  const home = val("homePhone");
  if (!hasAtLeastOneDemographicsPhone(patient)) {
    out.push({
      field: "mobilePhone",
      label: "Phone Number",
      message: "Enter at least one 10-digit phone number (mobile or home).",
    });
  } else {
    if (mobile && !isTenDigitPhone(mobile))
      out.push({ field: "mobilePhone", label: "Mobile Phone", message: "Enter a valid 10-digit mobile phone number." });
    if (home && !isTenDigitPhone(home))
      out.push({ field: "homePhone", label: "Home Phone", message: "Enter a valid 10-digit home phone number." });
  }

  if (!val("email"))
    out.push({ field: "email", label: "Email", message: "Email is required." });
  else if (!isValidEmail(val("email")))
    out.push({ field: "email", label: "Email", message: "Enter a valid email address." });

  if (!val("emerContact"))
    out.push({ field: "emerContact", label: "Emergency Contact", message: "Emergency contact name is required." });
  if (!val("emerRelation"))
    out.push({ field: "emerRelation", label: "Relationship", message: "Emergency contact relationship is required." });
  if (!val("emerPhone"))
    out.push({ field: "emerPhone", label: "Emergency Phone", message: "Emergency contact phone is required." });
  else if (!isTenDigitPhone(val("emerPhone")))
    out.push({ field: "emerPhone", label: "Emergency Phone", message: "Enter a valid 10-digit emergency contact phone number." });

  return out;
}

export function formatDobForInput(dob: string): string {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}/${y}`;
}

export function formatDobForApi(val: string): string {
  if (!val) return "";
  const parts = val.split("/");
  if (parts.length !== 3) return val;
  const [m, d, y] = parts;
  return `${y}-${m}-${d}`;
}

export const PHONE_MASK = (v: string) => v.replace(/\D/g, "").slice(0, 10);
export function formatPhoneDisplay(v: string): string {
  const d = v.replace(/\D/g, "");
  if (d.length >= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length >= 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length >= 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return d;
}

export const ZIP_MASK = (v: string) => v.replace(/\D/g, "").slice(0, 9);
export function zipDisplay(v: string): string {
  const d = v.replace(/\D/g, "");
  if (d.length >= 5) return `${d.slice(0, 5)}${d.length > 5 ? "-" + d.slice(5) : ""}`;
  return d;
}

/** Normalize API option: string or { id, description }. Returns { value, label } for <option>. */
export function optionValueLabel(
  item: string | { id?: number; description?: string },
  index: number
): { value: string; label: string } {
  if (typeof item === "string") return { value: item, label: item };
  const id = item.id ?? index;
  const desc = item.description ?? "";
  return { value: String(id), label: desc };
}

const SEX_OPTIONS = [
  { value: "F", label: "F" },
  { value: "M", label: "M" },
  { value: "X", label: "X" },
];

export interface UseDemographicsFormOptions {
  mode: ProfileInfoMode;
  /** Runs after a successful save. Awaited, so its own async work (e.g. case-history
   *  creation) keeps the form in its saving state and can block navigation on failure. */
  onSaveAndContinue?: () => void | Promise<void>;
  /** Called when a save is blocked by validation, so the parent can enter edit mode. */
  onValidationError?: () => void;
}

export function useDemographicsForm({
  mode,
  onSaveAndContinue,
  onValidationError,
}: UseDemographicsFormOptions) {
  const [loading, setLoading] = useState(true);
  /** Set when the load fails, so the form shows a retry instead of an empty (unsaved-looking) form. */
  const [loadError, setLoadError] = useState(false);
  /** Bumped by `reload` to re-run the load effect. */
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [patient, setPatient] = useState<Record<string, string>>({
    ...DEFAULT_PATIENT,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  /** Synchronous re-entry guard: rejects a second save before React re-renders the disabled button. */
  const savingRef = useRef(false);
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const {
    config,
    fetchDemographicsConfig,
    fetchDemographics,
    updateDemographics,
  } = useDemographicsStore();

  const isWizard = mode === "wizard";

  const states = config?.usStates ?? [];
  const maritalStatus = config?.maritalStatusList ?? [];
  const contactPreferenceOptions = config?.contactMethod ?? [];
  const employmentStatusOptions = config?.employmentStatusList ?? [];
  const emerRelationsOptions = config?.emerRelations ?? [];

  const stateOptions = useMemo(() => {
    return (Array.isArray(states) ? states : []).map((s) => {
      const code =
        typeof s === "object" && s !== null && "code" in s
          ? String((s as { code: string }).code)
          : String(s);
      return { value: code, label: code };
    });
  }, [states]);

  const maritalOptions = useMemo(
    () =>
      (Array.isArray(maritalStatus) ? maritalStatus : []).map((m, i) =>
        optionValueLabel(m as string | { id: number; description: string }, i)
      ),
    [maritalStatus]
  );

  const contactPrefOptions = useMemo(
    () =>
      (Array.isArray(contactPreferenceOptions) ? contactPreferenceOptions : []).map(
        (c, i) =>
          optionValueLabel(c as string | { id: number; description: string }, i)
      ),
    [contactPreferenceOptions]
  );

  const employmentOptions = useMemo(
    () =>
      (Array.isArray(employmentStatusOptions) ? employmentStatusOptions : []).map(
        (emp, i) =>
          optionValueLabel(emp as string | { id: number; description: string }, i)
      ),
    [employmentStatusOptions]
  );

  const emerRelationOpts = useMemo(
    () =>
      (Array.isArray(emerRelationsOptions) ? emerRelationsOptions : []).map(
        (r, i) =>
          optionValueLabel(r as string | { id: number; description: string }, i)
      ),
    [emerRelationsOptions]
  );

  /** Build payload for save; same shape for comparison. */
  const buildPayload = useCallback((p: Record<string, string>): IDemographics => {
    const mobileDigits = (p.mobilePhone ?? "").replace(/\D/g, "").slice(0, 10);
    const homeDigits = (p.homePhone ?? "").replace(/\D/g, "").slice(0, 10);
    return {
      ...p,
      dob: formatDobForApi(p.dob),
      emerPhone: (p.emerPhone ?? "").replace(/\D/g, "").slice(0, 10),
      mobilePhone: mobileDigits,
      homePhone: homeDigits,
      zip: (p.zip ?? "").replace(/\D/g, "").slice(0, 9),
      maritalStatID: p.maritalStatID ? Number(p.maritalStatID) : "",
      employStatID: p.employStatID ? Number(p.employStatID) : "",
    } as IDemographics;
  }, []);

  const lastSavedRef = useRef<string | null>(null);
  /** Bumps each time effect runs; only the latest load may clear the loading spinner (avoids stuck spinner when a prior effect aborts). */
  const loadGenRef = useRef(0);

  useEffect(() => {
    const facilityKey = getFacilityKey();
    const patientID = currentPatient?.patientID;
    if (!facilityKey || !patientID) {
      setLoading(false);
      return;
    }
    const loadGen = ++loadGenRef.current;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        await fetchDemographicsConfig();
        const data = await fetchDemographics();
        if (loadGen !== loadGenRef.current) return;
        if (!data) {
          setLoadError(true);
          return;
        }
        const p = { ...DEFAULT_PATIENT } as Record<string, string>;
        const raw = data as unknown as Record<string, unknown>;
        Object.keys(DEFAULT_PATIENT).forEach((k) => {
          const val = raw[k];
          if (val !== undefined && val !== null) p[k] = String(val);
        });
        if (raw.dob) p.dob = formatDobForInput(String(raw.dob));
        p.sex = normalizeSexFromApi(
          raw.sex ??
            raw.Sex ??
            raw.gender ??
            raw.Gender ??
            raw.patientSex ??
            raw.PatientSex
        );

        let mobileDigits = String(
          raw.mobilePhone ?? raw.cellPhone ?? ""
        ).replace(/\D/g, "");
        const homeDigits = String(raw.homePhone ?? "").replace(/\D/g, "");
        if (!mobileDigits && !homeDigits && raw.phone != null) {
          mobileDigits = String(raw.phone).replace(/\D/g, "").slice(0, 10);
        }
        p.mobilePhone = formatPhoneDisplay(mobileDigits);
        p.homePhone = formatPhoneDisplay(homeDigits);

        const emerDigits = String(raw.emerPhone ?? p.emerPhone ?? "")
          .replace(/\D/g, "")
          .slice(0, 10);
        p.emerPhone = formatPhoneDisplay(emerDigits);

        if (raw.employStatID != null) p.employStatID = String(raw.employStatID);
        if (raw.maritalStatID != null) p.maritalStatID = String(raw.maritalStatID);
        setPatient(p);
        lastSavedRef.current = JSON.stringify(buildPayload(p));
      } catch (e) {
        if (loadGen !== loadGenRef.current) return;
        console.error("Error fetching demographics", e);
        setLoadError(true);
      } finally {
        if (loadGen === loadGenRef.current) setLoading(false);
      }
    })();
  }, [currentPatient?.patientID, currentPatient?.facilityID, fetchDemographicsConfig, fetchDemographics, buildPayload, loadAttempt]);

  const reload = useCallback(() => setLoadAttempt((n) => n + 1), []);

  const update = (field: string, value: string) => {
    setPatient((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /** Move focus to (and scroll to) a field's input; waits for edit-mode inputs to mount. */
  const focusField = useCallback((field: string) => {
    const attempt = () => {
      const el = document.getElementById(`demographics-${field}`);
      if (el) {
        (el as HTMLElement).focus();
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(attempt));
  }, []);

  const requiredComplete = isRequiredDemographicsComplete(patient);

  /** Returns true when the save (or continue) succeeded; false when blocked or already in flight. */
  const save = async (): Promise<boolean> => {
    if (savingRef.current) return false;
    // Both intake and dashboard-profile editing require every field complete and
    // well-formed — emptying a required field must block the save in either place.
    const problems = collectOutstandingDemographics(patient);
    if (problems.length > 0) {
      const map: Record<string, string> = {};
      for (const e of problems) if (!map[e.field]) map[e.field] = e.message;
      setErrors(map);
      onValidationError?.();
      focusField(problems[0].field);
      toast.error(
        problems.length === 1
          ? problems[0].message
          : `Please complete ${problems.length} required fields.`
      );
      return false;
    }
    setErrors({});
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = buildPayload(patient);
      const payloadKey = JSON.stringify(payload);
      const hadChange = lastSavedRef.current !== payloadKey;

      if (hadChange) {
        await updateDemographics(payload);
        lastSavedRef.current = payloadKey;
        toast.success("Patient Info is updated successfully.");
      }
      // Awaited so downstream async work (case-history creation) stays under the saving
      // flag and can throw to block navigation.
      if (isWizard && onSaveAndContinue) await onSaveAndContinue();
      return true;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save();
    } catch {
      toast.error("Failed to update patient info.");
    }
  };

  const revertFields = (fields: Record<string, string>) => {
    setPatient((prev) => ({ ...prev, ...fields }));
  };

  return {
    patient,
    loading,
    loadError,
    reload,
    errors,
    saving,
    update,
    save,
    revertFields,
    handleSubmit,
    isWizard,
    requiredComplete,
    SEX_OPTIONS,
    stateOptions,
    maritalOptions,
    contactPrefOptions,
    employmentOptions,
    emerRelationOpts,
  };
}
